import React, { useMemo, useState } from 'react';
import { Calendar, Views } from 'react-big-calendar';
import dayjs from 'dayjs';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

import type { ILead } from 'src/services/api';
import type { IShowing } from 'src/api/landlord-showings-api';
import { useAuthenticatedState } from 'src/authenticated-state/context';
import { useProperties } from 'src/services/api';
import { useMobileCalendarDaySlot } from 'src/hooks/use-mobile-calendar-day-slot';
import {
    formatDateTimeForApi,
    getUserTimezone,
} from 'src/utils/format-date-time';
import {
    CANCELED_TOUR_COLOR,
} from 'src/pages/landlord/tours/components/calendar/property-colors';
import {
    buildPropertyCalendarMaps,
    getCalendarDayTimes,
    type ITourCalendarEvent,
    toCalendarWallClockDate,
    tourCalendarLocalizer,
} from 'src/pages/landlord/tours/components/calendar/tour-calendar-utils';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import styles from 'src/components/lead-sidebar/LeadSidebarDayCalendar.module.css';

interface Props {
    showings: IShowing[];
    lead: ILead;
    /**
     * Called when an empty slot is clicked, with the clicked time as an
     * API-ready ISO timestamp (already resolved in the user's configured
     * timezone) and the property currently selected in the filter (or the
     * lead's property), so the parent can open the create-tour modal prefilled.
     */
    onSchedule: (startIso: string, propertyId: number | null) => void;
}

function TourEventContent({ event }: { event: ITourCalendarEvent }) {
    return <div className={styles.eventContent}>{event.title}</div>;
}

/**
 * Day-view calendar for the lead message sidebar: a header with day navigation,
 * a clickable date that opens a mini month-picker, and a property filter that
 * defaults to the lead's property. Clicking an empty slot schedules a tour.
 */
export default function LeadSidebarDayCalendar({ showings, lead, onSchedule }: Props) {
    const { userSettings } = useAuthenticatedState();
    const { data: properties } = useProperties();
    const timezone = getUserTimezone(userSettings);

    const { propertyNameById, propertyColorById } = useMemo(
        () => buildPropertyCalendarMaps(properties ?? []),
        [properties]
    );
    const leadProperty = useMemo(
        () => (properties ?? []).find(
            (property) => property?.id === lead.property_id
        ),
        [properties, lead.property_id]
    );

    const [date, setDate] = useState<Date>(() => new Date());

    const [propertyFilter, setPropertyFilter] = useState<number | null>(
        lead.property_id ?? null
    );

    const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);

    const events = useMemo<ITourCalendarEvent[]>(() => {
        return showings
            .filter((s) =>
                propertyFilter === null || s.property_id === propertyFilter
            )
            .map((showing) => {
                const start = toCalendarWallClockDate(showing.start_time, timezone);
                const end = dayjs(start).add(30, 'minute').toDate();
                const propertyName = propertyNameById.get(showing.property_id) ?? 'Property';
                const leadName = lead.name || lead.email || 'Archived lead';
                return {
                    title: showing.is_canceled ?
                        `${propertyName} — Canceled tour` :
                        `${leadName} — ${propertyName}`,
                    start,
                    end,
                    showing,
                };
            });
    }, [showings, propertyFilter, timezone, propertyNameById, lead]);

    const eventPropGetter = (event: ITourCalendarEvent) => {
        const color = event.showing.is_canceled ?
            CANCELED_TOUR_COLOR :
            propertyColorById.get(event.showing.property_id) ?? CANCELED_TOUR_COLOR;
        return {
            className: `${styles.event} ${event.showing.is_canceled ? styles.eventCanceled : ''}`,
            // Only the per-property left-bar color is dynamic; the rest is in CSS.
            style: { borderLeftColor: color },
        };
    };

    const handleSelectSlot = (slotInfo: { start: Date }) => {
        const propertyId =
            propertyFilter ?? lead.property_id;
        // The grid is rendered in the user's timezone wall-clock, so interpret
        // the clicked wall-time in that timezone and emit an absolute ISO
        // timestamp. This avoids the browser-vs-user timezone shift that a plain
        // dayjs(date).format() would introduce.
        const wallTime = dayjs(slotInfo.start).format('YYYY-MM-DDTHH:mm:ss');
        onSchedule(formatDateTimeForApi(dayjs(wallTime), timezone), propertyId);
    };
    const calendarTimes = useMemo(() => getCalendarDayTimes(date, 12), [date]);
    const { isMobile, dayColumnWrapper } = useMobileCalendarDaySlot({
        min: calendarTimes.min,
        max: calendarTimes.max,
        onSelectSlot: (start) => handleSelectSlot({ start }),
        testId: 'mobile-lead-sidebar-tour-day-column',
    });

    return (
        <Box className={styles.card}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                className={styles.toolbar}
            >
                <Stack direction="row" alignItems="center" className={styles.dateControls}>
                    <IconButton
                        size="small"
                        aria-label="Previous day"
                        onClick={() => setDate(dayjs(date).subtract(1, 'day').toDate())}
                        data-testid="lead-sidebar-calendar-previous-day-button"
                    >
                        <ChevronLeft size={18} />
                    </IconButton>
                    <IconButton
                        size="small"
                        aria-label="Next day"
                        onClick={() => setDate(dayjs(date).add(1, 'day').toDate())}
                        data-testid="lead-sidebar-calendar-next-day-button"
                    >
                        <ChevronRight size={18} />
                    </IconButton>
                    <Button
                        onClick={(e) => setPickerAnchor(e.currentTarget)}
                        endIcon={<ChevronDown size={12} />}
                        className={styles.dateButton}
                        data-testid="lead-sidebar-calendar-date-button"
                    >
                        <span>{dayjs(date).format('ddd, MMM D')}</span>
                    </Button>
                </Stack>

                <Select
                    size="small"
                    value={propertyFilter === null ? '' : String(propertyFilter)}
                    onChange={(e) =>
                        setPropertyFilter(
                            e.target.value === '' ?
                                null :
                                Number(e.target.value)
                        )
                    }
                    className={styles.propertySelect}
                    data-testid="lead-sidebar-calendar-property-select"
                >
                    <MenuItem value="">All properties</MenuItem>
                    {leadProperty && (
                        <MenuItem value={String(leadProperty.id)}>
                            {leadProperty.name}
                        </MenuItem>
                    )}
                </Select>
            </Stack>

            <Popover
                open={Boolean(pickerAnchor)}
                anchorEl={pickerAnchor}
                onClose={() => setPickerAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateCalendar
                        value={dayjs(date)}
                        onChange={(value) => {
                            if (value) {
                                setDate(value.toDate());
                            }
                            setPickerAnchor(null);
                        }}
                    />
                </LocalizationProvider>
            </Popover>

            <Box className={styles.calendar}>
                <Calendar<ITourCalendarEvent>
                    localizer={tourCalendarLocalizer}
                    events={events}
                    startAccessor="start"
                    endAccessor="end"
                    date={date}
                    onNavigate={setDate}
                    view={Views.DAY}
                    views={[Views.DAY]}
                    toolbar={false}
                    selectable={!isMobile}
                    onSelectSlot={handleSelectSlot}
                    eventPropGetter={eventPropGetter}
                    components={{
                        event: TourEventContent,
                        dayColumnWrapper,
                    }}
                    onView={() => undefined}
                    min={calendarTimes.min}
                    max={calendarTimes.max}
                    scrollToTime={calendarTimes.scrollToTime}
                    step={30}
                    timeslots={2}
                    formats={{
                        timeGutterFormat: (value) => dayjs(value).format('h A'),
                    }}
                />
            </Box>
        </Box>
    );
}
